import { addMinutes, format, addDays } from "date-fns";
import type { Event, BookedTimeRange } from "./calendar-types";

export function generateHourlyTimeSlots(date: Date) {
  const slots: Date[] = [];
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  // Generate slots for every 30 minutes
  for (let i = 0; i < 48; i++) {
    const slot = new Date(dayStart);
    slot.setMinutes(i * 30);
    slots.push(slot);
  }

  return slots;
}

export function generateTimeOptions() {
  const options = [];
  const date = new Date();
  date.setHours(0, 0, 0, 0);

  for (let i = 0; i < 48; i++) {
    const time = addMinutes(date, i * 30);
    options.push({
      value: format(time, "HH:mm"),
      label: format(time, "h:mm a"),
    });
  }

  return options;
}

export function generateInitialEvents(): Event[] {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Set hours for today's events
  const meeting1Start = new Date(today);
  meeting1Start.setHours(10, 0, 0, 0);
  const meeting1End = new Date(today);
  meeting1End.setHours(11, 30, 0, 0);

  const meeting2Start = new Date(today);
  meeting2Start.setHours(14, 0, 0, 0);
  const meeting2End = new Date(today);
  meeting2End.setHours(15, 0, 0, 0);

  // Set hours for tomorrow's event
  const meeting3Start = new Date(tomorrow);
  meeting3Start.setHours(13, 0, 0, 0);
  const meeting3End = new Date(tomorrow);
  meeting3End.setHours(14, 30, 0, 0);

  return [
    {
      id: "1",
      title: "Team Meeting",
      description: "Weekly team sync to discuss project progress",
      start: meeting1Start.toISOString(),
      end: meeting1End.toISOString(),
      color: "hsl(var(--primary))",
    },
    {
      id: "2",
      title: "Client Call",
      description: "Discussion about new requirements",
      start: meeting2Start.toISOString(),
      end: meeting2End.toISOString(),
      color: "#10b981",
    },
    {
      id: "3",
      title: "Product Demo",
      description: "Showcase new features to the stakeholders",
      start: meeting3Start.toISOString(),
      end: meeting3End.toISOString(),
      color: "#f59e0b",
    },
  ];
}

// Generate sample booked time ranges
export function generateBookedTimeRanges(): BookedTimeRange[] {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dayAfterTomorrow = addDays(today, 2);

  // Create booked slots
  const booking1Start = new Date(today);
  booking1Start.setHours(9, 0, 0, 0);
  const booking1End = new Date(today);
  booking1End.setHours(10, 0, 0, 0);

  const booking2Start = new Date(tomorrow);
  booking2Start.setHours(11, 0, 0, 0);
  const booking2End = new Date(tomorrow);
  booking2End.setHours(12, 30, 0, 0);

  const booking3Start = new Date(tomorrow);
  booking3Start.setHours(15, 30, 0, 0);
  const booking3End = new Date(tomorrow);
  booking3End.setHours(16, 30, 0, 0);

  const booking4Start = new Date(dayAfterTomorrow);
  booking4Start.setHours(14, 0, 0, 0);
  const booking4End = new Date(dayAfterTomorrow);
  booking4End.setHours(15, 0, 0, 0);

  return [
    {
      id: "booked-1",
      title: "Doctor Appointment",
      startDate: booking1Start,
      endDate: booking1End,
      dayName: format(booking1Start, "EEEE"),
      bookedBy: "John Doe",
    },
    {
      id: "booked-2",
      title: "Client Meeting",
      startDate: booking2Start,
      endDate: booking2End,
      dayName: format(booking2Start, "EEEE"),
      bookedBy: "Jane Smith",
    },
    {
      id: "booked-3",
      title: "Dentist Appointment",
      startDate: booking3Start,
      endDate: booking3End,
      dayName: format(booking3Start, "EEEE"),
      bookedBy: "Alice Johnson",
    },
    {
      id: "booked-4",
      title: "Team Standup",
      startDate: booking4Start,
      endDate: booking4End,
      dayName: format(booking4Start, "EEEE"),
      bookedBy: "Team Lead",
    },
  ];
}

// Format time range for display
export function formatTimeRange(startDate: Date, endDate: Date) {
  return `${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")}`;
}

// Helper function to get a color based on the day name
export function getColorForDay(dayName: string): string {
  const colors = {
    Monday: "#4f46e5", // Indigo
    Tuesday: "#10b981", // Green
    Wednesday: "#f59e0b", // Amber
    Thursday: "#06b6d4", // Cyan
    Friday: "#8b5cf6", // Violet
    Saturday: "#ec4899", // Pink
    Sunday: "#ef4444", // Red
  };

  return colors[dayName as keyof typeof colors] || "#4f46e5";
}
